import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Client } from '../../types/client';

interface ClientDialogProps {
    open: boolean;
    client: Client | null;
    onClose: () => void;
    onSave: (client: Client) => void;
}

const initialClient: Omit<Client, 'id' | 'createdAt' | 'updatedAt'> = {
    name: '',
    description: ''
};

const ClientDialog: React.FC<ClientDialogProps> = ({ open, client, onClose, onSave }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState(initialClient);

    useEffect(() => {
        if (client) {
            setFormData({
                name: client.name,
                description: client.description || ''
            });
        } else {
            setFormData(initialClient);
        }
    }, [client]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        onSave({
            ...formData,
            id: client?.id || 0,
            createdAt: client?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {client ? t('manager.clients.edit') : t('manager.clients.new')}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                name="name"
                                label={t('manager.clients.fields.name')}
                                value={formData.name}
                                onChange={handleChange}
                                fullWidth
                                required
                                sx={{
                                    '& .MuiInputBase-input': {
                                        backgroundColor: 'transparent'
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="description"
                                label={t('manager.clients.fields.description')}
                                value={formData.description}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={3}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        backgroundColor: 'transparent'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ClientDialog; 